import React, { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Check,
  Code2,
  Download,
  FileImage,
  Globe2,
  Image,
  Layers,
  Link2,
  LogIn,
  Mail,
  Palette,
  Rocket,
  Share2,
  Sparkles,
  Table2,
  Target,
  Upload,
  Wand2,
  Zap,
  type LucideIcon,
} from "lucide-react";
import "./SnapLayerSalesLandingV3.css";

type IconComponent = LucideIcon;

type SmokeStyle = CSSProperties &
  Record<"--smoke-x" | "--smoke-y" | "--smoke-scale" | "--smoke-delay" | "--smoke-opacity", string>;

type FireStyle = CSSProperties &
  Record<"--fire-x" | "--fire-y" | "--fire-scale" | "--fire-delay" | "--fire-width" | "--fire-height", string>;

type Card = {
  icon: IconComponent;
  eyebrow?: string;
  title: string;
  description: string;
};

type Plan = {
  name: string;
  monthly: number;
  annual: number;
  description: string;
  featured?: boolean;
  features: string[];
};

type VisualAsset = {
  fileName: string;
  src: string;
  title: string;
  description: string;
};

const NAV_LINKS = [
  { href: "#how-it-works", label: "How it works" },
  { href: "#use-cases", label: "Use cases" },
  { href: "#features", label: "Features" },
  { href: "#pricing", label: "Pricing" },
];

const HERO_POINTS = [
  "Use the design you already have",
  "Fill rows with names, logos, offers, and screenshots",
  "Export images or send personal links",
];

const VALUE_CARDS: Card[] = [
  {
    icon: FileImage,
    eyebrow: "Start",
    title: "One design becomes a whole campaign",
    description: "Use a PSD, a template, or a design built in SnapLayer. Mark the parts that should change.",
  },
  {
    icon: Table2,
    eyebrow: "Fill",
    title: "A sheet handles the personal details",
    description: "Add names, companies, offers, URLs, screenshots, colors, and logos without rebuilding each image.",
  },
  {
    icon: Image,
    eyebrow: "Create",
    title: "Every row gets a polished image",
    description: "SnapLayer renders the variations for you, so each person sees creative that feels made for them.",
  },
  {
    icon: Link2,
    eyebrow: "Send",
    title: "Download, share, or publish",
    description: "Use image exports, SnapLinks, SnapPages, or automation paths when your workflow grows.",
  },
];

const WORKFLOW_STEPS = [
  {
    title: "Pick the creative",
    description: "Start with a finished design, imported PSD, or SnapLayer template.",
  },
  {
    title: "Choose what changes",
    description: "Turn text, logos, colors, images, screenshots, charts, or offers into fields.",
  },
  {
    title: "Add your rows",
    description: "Paste a list, upload a CSV, or use SnapSheet to keep the campaign organized.",
  },
  {
    title: "Launch the images",
    description: "Export a batch, send personal links, publish SnapPages, or connect the workflow later.",
  },
];

const USE_CASES: Card[] = [
  {
    icon: Mail,
    title: "Cold outreach that gets noticed",
    description: "Show a prospect their own logo, website, offer, or proof point inside the image.",
  },
  {
    icon: Share2,
    title: "Social posts at scale",
    description: "Create location, client, event, or product variations from one reusable template.",
  },
  {
    icon: BarChart3,
    title: "Simple proof cards",
    description: "Turn scores, stats, and comparisons into visual proof without building a full report.",
  },
  {
    icon: Globe2,
    title: "Personal landing moments",
    description: "Send each person to a SnapPage with their image instead of a generic attachment.",
  },
  {
    icon: Target,
    title: "Sales enablement",
    description: "Personalize proposal covers, demo graphics, capability cards, and follow-up visuals.",
  },
  {
    icon: Sparkles,
    title: "AI-assisted campaign prep",
    description: "Use AI to draft row copy, angle ideas, image directions, and short campaign messages.",
  },
];

const FEATURE_GROUPS: Card[] = [
  {
    icon: Upload,
    title: "Import the work you already made",
    description: "Bring in PSDs, image assets, and templates so the team does not start from a blank canvas.",
  },
  {
    icon: Palette,
    title: "Keep the design controlled",
    description: "Tune placement, colors, text, shadows, and image fit so personalization still looks designed.",
  },
  {
    icon: Download,
    title: "Export campaign-ready images",
    description: "Create polished files for email, social, ads, proposals, sales follow-up, and client delivery.",
  },
  {
    icon: Code2,
    title: "Automate when it is worth it",
    description: "The visual workflow comes first. API and builder paths are there when repeat work should disappear.",
  },
];

const ADVANCED_FEATURES = [
  "SnapLinks for one personal image link per person",
  "SnapPages for a simple branded destination",
  "SnapSheet for spreadsheet-style campaign rows",
  "PSD import and export for creative teams",
  "Logo lookup, website screenshots, and image fields",
  "API Builder for partner, team, and automation workflows",
];

const PRICING_PLANS: Plan[] = [
  {
    name: "Solo",
    monthly: 29,
    annual: 23,
    description: "For creators testing personalized campaign images.",
    features: ["10 templates", "50 SnapSheet rows", "Core image personalization", "BYOK AI connections"],
  },
  {
    name: "Plus",
    monthly: 79,
    annual: 63,
    description: "For small teams sending regular visual campaigns.",
    features: ["25 templates", "100 SnapSheet rows", "SnapLinks", "PSD import and export"],
  },
  {
    name: "Pro",
    monthly: 149,
    annual: 119,
    description: "For agencies building repeatable client campaigns.",
    featured: true,
    features: ["50 templates", "250 SnapSheet rows", "SnapPages", "Team and sub-account paths"],
  },
  {
    name: "Agency",
    monthly: 249,
    annual: 199,
    description: "For high-volume teams running campaigns across clients.",
    features: ["100 templates", "1,000 SnapSheet rows", "25 SnapPages", "Expanded team and account paths"],
  },
];

const VISUAL_ASSETS: VisualAsset[] = [
  {
    fileName: "hero-personalized-campaign.webp",
    src: "/marketing/home/hero-personalized-campaign.svg",
    title: "One design, many campaign images",
    description: "Hero visual showing a single creative turning into personalized variations.",
  },
  {
    fileName: "spreadsheet-to-images-flow.webp",
    src: "/marketing/home/spreadsheet-to-images-flow.svg",
    title: "Spreadsheet to images",
    description: "Rows become finished creative without rebuilding each design.",
  },
  {
    fileName: "before-after-template-stack.webp",
    src: "/marketing/home/before-after-template-stack.svg",
    title: "Before and after template stack",
    description: "A generic template becomes a set of useful personal visuals.",
  },
  {
    fileName: "outreach-use-case.webp",
    src: "/marketing/home/outreach-use-case.svg",
    title: "Outreach use case",
    description: "Personalized image previews for email, DMs, and follow-up.",
  },
  {
    fileName: "snaplinks-personal-page.webp",
    src: "/marketing/home/snaplinks-personal-page.svg",
    title: "SnapLinks and SnapPages",
    description: "A personal image can also live on a shareable page.",
  },
  {
    fileName: "agency-proof-card.webp",
    src: "/marketing/home/agency-proof-card.svg",
    title: "Agency proof card",
    description: "Visual proof points, scores, charts, and screenshots in one card.",
  },
  {
    fileName: "psd-to-template-layers.webp",
    src: "/marketing/home/psd-to-template-layers.svg",
    title: "PSD to reusable template",
    description: "Creative layers become controllable campaign fields.",
  },
];

const FAQS = [
  {
    question: "Do I need to code to use SnapLayer?",
    answer: "No. The main workflow is visual: make a template, add rows, and generate images. Developers can automate later.",
  },
  {
    question: "What makes it different from a normal design tool?",
    answer: "A design tool makes one image. SnapLayer turns one design into many personalized images from your data.",
  },
  {
    question: "Can I use designs my team already made?",
    answer: "Yes. Start with PSDs, uploaded assets, or existing templates, then make the important parts dynamic.",
  },
  {
    question: "Where do the finished images go?",
    answer: "Download them, send SnapLinks, publish SnapPages, or connect the workflow to your existing campaign tools.",
  },
];

const ROCKET_SMOKE: SmokeStyle[] = Array.from({ length: 72 }, (_, index) => {
  const row = Math.floor(index / 18);
  const col = index % 18;
  const centerOffset = col - 8.5;
  const spread = 10 + row * 17;
  const smokeX = `${centerOffset * spread}px`;
  const smokeY = `${32 + row * 30 + (col % 4) * 9}px`;
  const smokeScale = `${0.92 + ((index * 5) % 14) / 10}`;
  const smokeDelay = `${(index % 18) * 0.065 + row * 0.04}s`;
  const smokeOpacity = `${0.38 + ((index * 3) % 8) / 20}`;

  return {
    "--smoke-x": smokeX,
    "--smoke-y": smokeY,
    "--smoke-scale": smokeScale,
    "--smoke-delay": smokeDelay,
    "--smoke-opacity": smokeOpacity,
  };
});

const FIRE_PARTICLES: FireStyle[] = Array.from({ length: 28 }, (_, index) => {
  const side = (index % 7) - 3;
  const band = Math.floor(index / 7);
  const fireX = `${side * (4 + band * 3)}px`;
  const fireY = `${24 + band * 15 + (index % 3) * 7}px`;
  const fireScale = `${0.85 + ((index * 4) % 10) / 10}`;
  const fireDelay = `${(index % 11) * 0.035}s`;
  const fireWidth = `${9 + (index % 5) * 3}px`;
  const fireHeight = `${16 + band * 6 + (index % 4) * 4}px`;

  return {
    "--fire-x": fireX,
    "--fire-y": fireY,
    "--fire-scale": fireScale,
    "--fire-delay": fireDelay,
    "--fire-width": fireWidth,
    "--fire-height": fireHeight,
  };
});

const RocketLaunchV3: React.FC = () => {
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    const launchTimer = window.setTimeout(() => setLaunched(true), 900);
    return () => window.clearTimeout(launchTimer);
  }, []);

  return (
    <div className={`sl-v3-rocket-stage ${launched ? "is-launched" : ""}`} aria-hidden="true">
      <div className="sl-v3-rocket-field">
        <img src="/assets/rocket/TowerPad_512.png" alt="" className="sl-v3-rocket-tower" />
        <div className="sl-v3-rocket-ship">
          <img src="/assets/rocket/Rocket_512.png" alt="" className="sl-v3-rocket-body" />
          <div className="sl-v3-fire-field">
            {FIRE_PARTICLES.map((style, index) => (
              <span key={index} className="sl-v3-fire-particle" style={style} />
            ))}
          </div>
        </div>
        <div className="sl-v3-smoke-field">
          {ROCKET_SMOKE.map((style, index) => (
            <span key={index} className="sl-v3-smoke-puff" style={style} />
          ))}
        </div>
      </div>
    </div>
  );
};

const IconCard: React.FC<{ card: Card }> = ({ card }) => {
  const Icon = card.icon;

  return (
    <article className="sl-v3-card">
      <div className="sl-v3-card-icon">
        <Icon aria-hidden="true" />
      </div>
      {card.eyebrow && <span className="sl-v3-card-eyebrow">{card.eyebrow}</span>}
      <h3>{card.title}</h3>
      <p>{card.description}</p>
    </article>
  );
};

const CampaignPreview: React.FC = () => (
  <div className="sl-v3-campaign-preview" aria-label="Example personalized campaign preview">
    <div className="sl-v3-preview-toolbar">
      <span />
      <span />
      <span />
      <strong>Campaign builder</strong>
    </div>
    <div className="sl-v3-preview-body">
      <div className="sl-v3-preview-card">
        <div className="sl-v3-preview-logo">Logo</div>
        <div className="sl-v3-preview-title">A personal visual for each row</div>
        <div className="sl-v3-preview-proof">
          <BarChart3 aria-hidden="true" />
          <span>Proof point</span>
        </div>
      </div>
      <div className="sl-v3-preview-sheet">
        {["Name", "Company", "Offer", "Website"].map((label) => (
          <span key={label}>{label}</span>
        ))}
        {["Mia", "Northstar", "Audit", "Preview"].map((label) => (
          <span key={label}>{label}</span>
        ))}
        {["Ari", "BluePeak", "Demo", "Preview"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </div>
  </div>
);

const SnapLayerSalesLandingV3: React.FC = () => {
  const [annualBilling, setAnnualBilling] = useState(false);

  return (
    <main className="sl-v3">
      <aside className="sl-v3-ltd-banner" aria-label="SnapLayer lifetime deal">
        <span>Limited-time lifetime deal</span>
        <strong>Do not want another monthly subscription?</strong>
        <a href="https://getsnaplayer.com/" target="_blank" rel="noreferrer">
          Get lifetime access
          <ArrowRight aria-hidden="true" />
        </a>
      </aside>

      <nav className="sl-v3-nav" aria-label="SnapLayer version 3 navigation">
        <a href="#top" className="sl-v3-brand" aria-label="SnapLayer home">
          <img src="/brand/snaplayer-wordmark-transparent.png" alt="SnapLayer" />
        </a>
        <div className="sl-v3-nav-links">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
        </div>
        <div className="sl-v3-nav-actions">
          <Link to="/login" className="sl-v3-ghost-button">
            <LogIn aria-hidden="true" />
            Log in
          </Link>
          <a href="https://getsnaplayer.com/" className="sl-v3-solid-button" target="_blank" rel="noreferrer">
            Lifetime deal
            <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </nav>

      <section id="top" className="sl-v3-hero">
        <div className="sl-v3-hero-copy">
          <span className="sl-v3-pill">Personalized campaign images</span>
          <h1>Create a personal image for every prospect.</h1>
          <p>
            Start with one design. Add a sheet of names, logos, offers, websites, and screenshots. SnapLayer turns
            each row into a polished image you can download, send, or publish as a personal link.
          </p>
          <div className="sl-v3-hero-actions">
            <a href="https://getsnaplayer.com/" className="sl-v3-primary-cta" target="_blank" rel="noreferrer">
              Get the lifetime deal
              <ArrowRight aria-hidden="true" />
            </a>
            <a href="#how-it-works" className="sl-v3-secondary-cta">
              See how it works
            </a>
          </div>
          <div className="sl-v3-hero-points">
            {HERO_POINTS.map((point) => (
              <span key={point}>
                <Check aria-hidden="true" />
                {point}
              </span>
            ))}
          </div>
        </div>

        <div className="sl-v3-hero-visual">
          <RocketLaunchV3 />
          <CampaignPreview />
          <div className="sl-v3-floating-badge sl-v3-floating-badge-one">
            <Layers aria-hidden="true" />
            One template
          </div>
          <div className="sl-v3-floating-badge sl-v3-floating-badge-two">
            <Zap aria-hidden="true" />
            Many images
          </div>
        </div>
      </section>

      <section className="sl-v3-proof-strip" aria-label="SnapLayer value summary">
        {VALUE_CARDS.map((card) => (
          <IconCard key={card.title} card={card} />
        ))}
      </section>

      <section className="sl-v3-section sl-v3-section-light sl-v3-visual-story" aria-labelledby="sl-v3-visual-story-title">
        <div className="sl-v3-section-heading">
          <span className="sl-v3-pill">Campaign visuals</span>
          <h2 id="sl-v3-visual-story-title">Show the campaign story before anyone reads a feature list.</h2>
          <p>
            The page is built around visual proof: one design, clean row data, personal image variations, and shareable
            campaign delivery.
          </p>
        </div>
        <div className="sl-v3-visual-grid">
          {VISUAL_ASSETS.map((asset) => (
            <article key={asset.fileName} className="sl-v3-visual-card">
              <img src={asset.src} alt={asset.title} loading="lazy" />
              <div>
                <span>Visual direction</span>
                <h3>{asset.title}</h3>
                <p>{asset.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="how-it-works" className="sl-v3-section sl-v3-section-light">
        <div className="sl-v3-section-heading">
          <span className="sl-v3-pill">How it works</span>
          <h2>Simple enough for a marketer. Flexible enough for a team.</h2>
          <p>No code is needed for the core workflow. Build the template, add rows, and launch the images.</p>
        </div>
        <div className="sl-v3-workflow">
          {WORKFLOW_STEPS.map((step, index) => (
            <article key={step.title} className="sl-v3-step">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="use-cases" className="sl-v3-section sl-v3-section-ink">
        <div className="sl-v3-section-heading">
          <span className="sl-v3-pill">Use cases</span>
          <h2>Use SnapLayer anywhere a personal visual would get more attention.</h2>
          <p>Lead with image campaigns, then grow into links, pages, reports, and automation when the workflow proves itself.</p>
        </div>
        <div className="sl-v3-use-case-grid">
          {USE_CASES.map((card) => (
            <IconCard key={card.title} card={card} />
          ))}
        </div>
      </section>

      <section id="features" className="sl-v3-section sl-v3-section-light sl-v3-split-section">
        <div className="sl-v3-split-copy">
          <span className="sl-v3-pill">What makes it useful</span>
          <h2>Personalization that still looks designed.</h2>
          <p>
            SnapLayer is not just mail merge for images. You control the layers, layout, image fit, colors, and proof
            points so the result feels intentional.
          </p>
          <div className="sl-v3-feature-list">
            {ADVANCED_FEATURES.map((feature) => (
              <span key={feature}>
                <Check aria-hidden="true" />
                {feature}
              </span>
            ))}
          </div>
        </div>
        <div className="sl-v3-feature-grid">
          {FEATURE_GROUPS.map((card) => (
            <IconCard key={card.title} card={card} />
          ))}
        </div>
      </section>

      <section className="sl-v3-section sl-v3-ai-band">
        <div>
          <span className="sl-v3-pill">AI as an assistant</span>
          <h2>Use AI for blanks, not lock-in.</h2>
          <p>
            Draft campaign angles, clean up row copy, write short CTAs, and create image directions. Bring your own
            provider keys when you want direct control.
          </p>
        </div>
        <div className="sl-v3-ai-cards">
          {[
            "Generate headline options",
            "Clean up spreadsheet rows",
            "Write short offer copy",
            "Create image prompt ideas",
          ].map((item) => (
            <span key={item}>
              <Wand2 aria-hidden="true" />
              {item}
            </span>
          ))}
        </div>
      </section>

      <section id="pricing" className="sl-v3-section sl-v3-section-light">
        <div className="sl-v3-section-heading">
          <span className="sl-v3-pill">Pricing</span>
          <h2>Monthly subscriptions for regular campaign work.</h2>
          <p>
            Start monthly, switch to annual when SnapLayer becomes part of your regular workflow, or use the limited-time
            lifetime deal if you would rather avoid another subscription.
          </p>
          <div className="sl-v3-billing-toggle" aria-label="Billing period">
            <button type="button" data-active={!annualBilling} onClick={() => setAnnualBilling(false)}>
              Monthly
            </button>
            <button type="button" data-active={annualBilling} onClick={() => setAnnualBilling(true)}>
              Annual
              <span>2 months free</span>
            </button>
          </div>
        </div>
        <div className="sl-v3-pricing-grid">
          {PRICING_PLANS.map((plan) => {
            const price = annualBilling ? plan.annual : plan.monthly;

            return (
              <article key={plan.name} className="sl-v3-plan" data-featured={plan.featured ? "true" : "false"}>
                <div className="sl-v3-plan-top">
                  <h3>{plan.name}</h3>
                  {plan.featured && <span>Best fit</span>}
                </div>
                <div className="sl-v3-plan-price">
                  <strong>${price}</strong>
                  <span>/mo</span>
                </div>
                {annualBilling && <div className="sl-v3-plan-note">Billed annually</div>}
                <p>{plan.description}</p>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <Check aria-hidden="true" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a href="https://getsnaplayer.com/" target="_blank" rel="noreferrer">
                  See plan
                  <ArrowRight aria-hidden="true" />
                </a>
              </article>
            );
          })}
        </div>
        <div className="sl-v3-ltd-pricing-callout">
          <div>
            <span>Lifetime option</span>
            <strong>Do not want to pay monthly?</strong>
            <p>Get a SnapLayer lifetime deal for a limited time while the launch offer is available.</p>
          </div>
          <a href="https://getsnaplayer.com/" target="_blank" rel="noreferrer">
            View lifetime deal
            <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="sl-v3-section sl-v3-section-faq">
        <div className="sl-v3-section-heading">
          <span className="sl-v3-pill">Quick answers</span>
          <h2>Plain answers for first-time visitors.</h2>
        </div>
        <div className="sl-v3-faq-grid">
          {FAQS.map((faq) => (
            <article key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sl-v3-final-cta">
        <div>
          <Rocket aria-hidden="true" />
          <span className="sl-v3-pill">Ready to launch</span>
          <h2>Turn one good design into a campaign full of personal images.</h2>
          <p>Use SnapLayer when generic campaign creative is not specific enough to earn attention.</p>
          <div className="sl-v3-hero-actions">
            <a href="https://getsnaplayer.com/" className="sl-v3-primary-cta" target="_blank" rel="noreferrer">
              Get the lifetime deal
              <ArrowRight aria-hidden="true" />
            </a>
            <Link to="/login" className="sl-v3-secondary-cta">
              Log in
              <LogIn aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SnapLayerSalesLandingV3;
